import React, { useEffect, useReducer, useState } from 'react'
import { useAccessRequestPolicies, useAddItemsToCart, useCart } from 'hooks'
import {
    Box,
    Checkbox,
    styled,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material'
import clsx from 'clsx'
import { ItemDetails } from 'components'
import { Trans, useTranslation } from 'react-i18next'
import { Icon } from 'packages/eid-icons'
import {
    Loader,
    Pagination,
    ThresholdSearchInput,
    Tooltip,
} from 'packages/eid-ui'
import { isNilOrEmpty, useIsSmallScreen } from 'packages/core'
import { useResourceTypeContext } from 'resourceTypeContext'
import { reducers } from './reducers'
import { SuggestedRole } from './SuggestedRole'

const DesktopContainer = styled(Box)({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '32px',
    '& >button': {
        '& >div': { width: '185px !important' },
    },
})
const MobileContainer = styled('div')({
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: '32px',
    '& >button': {
        '& >div': { width: '327px !important' },
    },
})

const StyledTable = styled(Table)(({ theme }) => ({
    backgroundColor: theme?.palette?.background?.paper,
    '& tr:hover': {
        backgroundColor: '#f7f8fa',
    },
}))

const StyledTableRow = styled(TableRow)(() => ({
    border: ' solid 1px #efeff1',
}))

const StyledTableCell = styled(TableCell)(() => ({
    overflowWrap: 'break-word',
    maxWidth: '220px',
    wordBreak: 'break-word',
    overflow: 'hidden',
    padding: '14px 10px',
    fontSize: '13px',
    border: 'none',
    borderBottom: '0',
    '&:first-of-type': {
        paddingLeft: '31px',
    },
}))

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
    overflowWrap: 'break-word',
    maxWidth: '220px',
    wordBreak: 'break-word',
    textTransform: 'uppercase',
    overflow: 'hidden',
    lineHeight: '15px',
    fontSize: '12px',
    padding: '14px 10px',
    backgroundImage:
        'linear-gradient(0deg, rgb(0, 0, 0, 0.02) 12.5%, rgb(255, 255, 255) 12.5%, rgb(255, 255, 255) 50%, rgb(0, 0, 0, 0.02) 50%, rgb(0, 0, 0, 0.02) 62.5%, rgb(255, 255, 255) 62.5%, rgb(255, 255, 255) 100%)',
    backgroundSize: '8.00px 8.00px',
    '& svg': {
        margin: '0px !important',
    },
}))

const GreyText = styled('span')(() => ({
    color: '#7d7c7c !important',
}))

const take = 10

const SuggestedRoles = ({
    resource,
    differentiationValue,
    targetPerson,
    onDataLoaded,
    useDataHook,
    title,
}) => {
    const [{ referencePerson }] = useResourceTypeContext()

    const [selectedItems, dispatch] = useReducer(reducers, [])

    const theme = useTheme()

    const { t } = useTranslation()
    const isSmallScreen = useIsSmallScreen()

    const { data: cart } = useCart()

    const [page, setPage] = useState(1)
    const [perPageItems, setPerPageItems] = useState(take)
    const [searchKey, setSearchKey] = useState('')

    const handlePageChange = (_, value) => {
        setPage(value)
    }
    const handlePageSelection = (value) => {
        setPage(value)
    }
    const handleItemsPerPageChange = (value) => {
        setPage(1)
        setPerPageItems(value)
    }

    const { data } = useDataHook(
        resource.id,
        targetPerson.id,
        referencePerson?.id,
        (page - 1) * perPageItems,
        perPageItems,
        searchKey && encodeURIComponent(searchKey),
        differentiationValue?.id,
    )

    useEffect(() => {
        if (data && onDataLoaded) {
            onDataLoaded(data)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    const isLoading = !Boolean(data)

    const list = data ? data.data : undefined

    const { mutateAsync: addItemsToCart, isLoading: isAddingItems } =
        useAddItemsToCart()

    const { data: requestAccessPolicies } = useAccessRequestPolicies()

    if (!cart || !requestAccessPolicies) {
        return <Loader />
    }

    const isItemAlreadyInCart = (id) =>
        Boolean(
            cart.cartItems.find((item) => item.requestableResourceId === id),
        )

    const error = selectedItems.some(
        (r) =>
            r.timeConstraintActive &&
            (isNilOrEmpty(r.endDateUtc) ||
                r.startDateUtc.isAfter(r.endDateUtc) ||
                (r.policy?.isTimeConstrained &&
                    r.policy?.maximumValueInMinutes &&
                    r.endDateUtc?.isAfter(
                        r.startDateUtc
                            .clone()
                            .add(r.policy?.maximumValueInMinutes, 'minutes'),
                    ))),
    )

    const attributes = [
        {
            label: t('Common_FriendlyName'),
            resolve: (item) => <>{item.friendlyName}</>,
        },
        {
            label: t('Common_Description'),
            resolve: (item) => <>{item.description}</>,
        },
        {
            name: 'resourceSystemFriendlyName',
            label: t('Common_ResourceSystem'),
        },
    ]

    const proceedToCartButton = (
        <Tooltip
            title={
                selectedItems.length === 0
                    ? t('Common_NoItemSelected')
                    : error
                    ? t('Common_InvalidDatesForItems')
                    : ''
            }
        >
            <Box maxWidth="300px" minWidth="200px">
                <ItemDetails.ActionButton
                    width="fit-content"
                    minWidth="100%"
                    height="48px"
                    fontColor={theme?.palette?.common?.white}
                    bgColor={theme?.palette?.primary?.main}
                    disabled={selectedItems.length === 0 || error}
                    loading={isAddingItems}
                    onClick={() => {
                        addItemsToCart(selectedItems).then(() =>
                            dispatch({
                                type: 'REMOVE_ALL_ITEMS',
                                payload: filterItems(data, cart.cartItems).map(
                                    (d) => d.id,
                                ),
                            }),
                        )
                    }}
                >
                    <Box display="flex" alignItems="center">
                        <Icon
                            name="AddToCart"
                            color={theme?.palette?.common?.white}
                        />
                    </Box>
                    <Box display="flex" alignItems="center" marginLeft="10px">
                        <Typography
                            style={{ color: theme?.palette?.common?.white }}
                        >
                            {t('Common_AddAllToCart', {
                                items: selectedItems.length,
                                count: selectedItems.length,
                            })}
                        </Typography>
                    </Box>
                </ItemDetails.ActionButton>
            </Box>
        </Tooltip>
    )

    const numberOfPages = data ? Math.ceil(data.totalCount / perPageItems) : 0

    const rowSelection = true
    const selection = selectedItems.map((i) => i.requestableResourceId)
    const headings = attributes.map((a) => `${a.label}`)

    const isSelected = (id) => selection.indexOf(id) !== -1

    const isTimeConstrained = (item) =>
        selectedItems.find((i) => i.requestableResourceId === item.id)
            ?.timeConstraintActive ?? false

    const filterItems = (data, cartItems) => {
        const newItems = []
        for (let i = 0; i < data.length; i++) {
            const currentItem = data[i]
            const index = cartItems.findIndex(
                (item) => item.requestableResourceId === currentItem.id,
            )

            if (index === -1 && !currentItem.isAssigned) {
                newItems.push({
                    ...currentItem,
                    policy: requestAccessPolicies[currentItem.requestPolicyId],
                })
            }
        }
        return newItems
    }

    const handleSearchChange = (value) => {
        setPage(1)
        setSearchKey(value)
    }

    const getSelectedItem = (itemId) =>
        selectedItems.find((i) => i.requestableResourceId === itemId)

    return (
        <>
            <Box
                width="100%"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                padding="32px 32px 16px 32px"
            >
                <Typography
                    style={{
                        fontWeight: 'bold',
                        fontSize: '14px',
                        marginRight: '10px',
                        wordBreak: 'break-word',
                    }}
                >
                    {title}
                    {data ? (
                        <Box
                            component="span"
                            style={{
                                fontSize: '14px',
                                color: '#aab0b4',
                            }}
                            marginLeft="8px"
                        >
                            {data.totalCount}
                        </Box>
                    ) : (
                        ''
                    )}
                </Typography>

                <Box width="308px" alignItems="center">
                    <ThresholdSearchInput
                        placeholder={'Common_Search'}
                        handleSearch={handleSearchChange}
                    />
                </Box>
            </Box>

            <StyledTable>
                <TableHead>
                    <StyledTableRow>
                        {data && rowSelection && (
                            <StyledTableHeaderCell
                                component={StyledTableCell}
                                padding="checkbox"
                            >
                                <GreyText>
                                    {filterItems(data, cart.cartItems).length >
                                        0 && (
                                        <Checkbox
                                            icon={<Icon name="CheckBox" />}
                                            checkedIcon={
                                                <Icon
                                                    name="CheckedBox"
                                                    color={
                                                        theme?.palette?.primary
                                                            ?.main
                                                    }
                                                />
                                            }
                                            checked={
                                                data.length > 0 &&
                                                filterItems(
                                                    data,
                                                    cart.cartItems,
                                                ).every(
                                                    (el) =>
                                                        selectedItems.findIndex(
                                                            (s) =>
                                                                s.requestableResourceId ===
                                                                el.id,
                                                        ) > -1,
                                                )
                                            }
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    dispatch({
                                                        type: 'ADD_ALL_ITEMS',
                                                        payload: {
                                                            items: filterItems(
                                                                data,
                                                                cart.cartItems,
                                                            ),
                                                            targetPersonId:
                                                                targetPerson.id,
                                                        },
                                                    })
                                                } else {
                                                    dispatch({
                                                        type: 'REMOVE_ALL_ITEMS',
                                                        payload: filterItems(
                                                            data,
                                                            cart.cartItems,
                                                        ).map((d) => d.id),
                                                    })
                                                }
                                            }}
                                        />
                                    )}
                                </GreyText>
                            </StyledTableHeaderCell>
                        )}

                        {headings.map((heading, index) => (
                            <StyledTableHeaderCell
                                key={index}
                                component={StyledTableCell}
                                align={heading.align}
                                data-protectedsubcomponent={
                                    heading?.requireAccess?.control || ''
                                }
                            >
                                <GreyText>{heading}</GreyText>
                            </StyledTableHeaderCell>
                        ))}
                        <StyledTableHeaderCell
                            key="setduration"
                            component={StyledTableCell}
                        >
                            <GreyText />
                        </StyledTableHeaderCell>
                    </StyledTableRow>
                </TableHead>
                <TableBody>
                    {isLoading && (
                        <TableRow>
                            <TableCell
                                colSpan={headings.length + 2}
                                style={{ textAlign: 'center' }}
                            >
                                <Loader />
                            </TableCell>
                        </TableRow>
                    )}
                    {list &&
                        (list.length === 0 ? (
                            <StyledTableRow>
                                <StyledTableCell
                                    colSpan={headings.length + 2}
                                    style={{ textAlign: 'center' }}
                                >
                                    <Trans i18nKey="Common_NoDataFound" />
                                </StyledTableCell>
                            </StyledTableRow>
                        ) : (
                            <>
                                {list.map((item, index) => (
                                    <SuggestedRole
                                        key={`${item.id}${index}`}
                                        headings={headings}
                                        attributes={attributes}
                                        item={item}
                                        isTimeConstrained={isTimeConstrained}
                                        getSelectedItem={getSelectedItem}
                                        dispatch={dispatch}
                                        isItemAlreadyInCart={
                                            isItemAlreadyInCart
                                        }
                                        isSelected={isSelected}
                                        targetPerson={targetPerson}
                                    />
                                ))}
                            </>
                        ))}
                </TableBody>
            </StyledTable>

            {isSmallScreen ? (
                <>
                    <Box
                        width="100%"
                        padding="16px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {!isLoading && (
                            <Pagination
                                count={numberOfPages}
                                size="small"
                                page={page}
                                onChange={handlePageChange}
                                totalCount={data?.totalCount}
                                onPageSelection={handlePageSelection}
                                itemsPerPage={perPageItems}
                                onItemsPerPageChange={handleItemsPerPageChange}
                                showPageSelection={true}
                                showItemsPerPageSelection
                                showCountStat
                            />
                        )}
                    </Box>

                    <MobileContainer>{proceedToCartButton}</MobileContainer>
                </>
            ) : (
                <DesktopContainer
                    justifyContent={
                        data && numberOfPages > 1 ? 'space-between' : 'flex-end'
                    }
                >
                    {data && !isLoading && (
                        <Pagination
                            count={numberOfPages}
                            size="large"
                            page={page}
                            onChange={handlePageChange}
                            totalCount={data?.totalCount}
                            onPageSelection={handlePageSelection}
                            itemsPerPage={perPageItems}
                            onItemsPerPageChange={handleItemsPerPageChange}
                            showPageSelection={true}
                            showItemsPerPageSelection
                            showCountStat
                        />
                    )}

                    {proceedToCartButton}
                </DesktopContainer>
            )}
        </>
    )
}

export default SuggestedRoles
